import React, { useRef, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import * as THREE from "three";
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';

export default function ThreeDViewer({ isActive = true, selectedProperty }) {
  const mountRef = useRef(null);
  const sceneRef = useRef(null);
  const rendererRef = useRef(null);
  const cameraRef = useRef(null);
  const controlsRef = useRef(null);
  const animationRef = useRef(null);
  const lightingSystemRef = useRef(null);
  const materialsRef = useRef(null);
  const windowGlassesRef = useRef([]);
  
  const [debugStatus, setDebugStatus] = useState({
    containerReady: false,
    threeJsRunning: false,
    materialsLoaded: false,
    lightingActive: false,
    sceneBuilt: false,
    controlsConnected: false,
    error: null
  });

  // Functional Control Panel State
  const [controlPanel, setControlPanel] = useState({
    isExpanded: true,
    lightingIntensity: 1.2,
    ambientIntensity: 0.4,
    windowTransparency: 0.3, // Higher default for brighter windows
    materialQuality: 'premium',
    shadowSoftness: 0.8,
    timeOfDay: 0.7,
    cameraHeight: 1.6
  });

  useEffect(() => {
    if (!isActive || !mountRef.current) return;

    console.log("🚪 Initialisiere 3D Viewer mit Eingangstür...");
    setDebugStatus(prev => ({ ...prev, containerReady: true, error: null }));

    const initViewerWithEntrance = async () => {
      try {
        const container = mountRef.current;
        const width = container.clientWidth;
        const height = container.clientHeight;

        // Scene setup
        const scene = new THREE.Scene();
        scene.background = new THREE.Color(0xf5f5f0);
        scene.fog = new THREE.Fog(0xf5f5f0, 50, 200);
        sceneRef.current = scene;

        // Camera setup
        const camera = new THREE.PerspectiveCamera(45, width / height, 0.1, 1000);
        camera.position.set(12, controlPanel.cameraHeight + 3, 12);
        cameraRef.current = camera;

        // MAXIMUM QUALITY RENDERER - Focus on Visual Quality
        const renderer = new THREE.WebGLRenderer({ 
          antialias: true, // Strong anti-aliasing
          alpha: true,
          powerPreference: "high-performance",
          precision: "highp", // Maximum precision
          logarithmicDepthBuffer: true, // Better depth precision
          preserveDrawingBuffer: true, // For screenshots
          stencil: false, // Performance optimization
          depth: true // Better depth testing
        });
        renderer.setSize(width, height);
        renderer.setPixelRatio(Math.min(window.devicePixelRatio, 1.5)); // Smart DPI - not too high
        
        // EMERGENCY FIX - NO SHADOWS AT ALL
        renderer.shadowMap.enabled = false; // COMPLETELY DISABLE SHADOWS
        // renderer.shadowMap.type = THREE.PCFSoftShadowMap;
        // renderer.shadowMap.autoUpdate = true;
        
        // PROFESSIONAL COLOR MANAGEMENT
        renderer.outputColorSpace = THREE.SRGBColorSpace;
        renderer.toneMapping = THREE.ACESFilmicToneMapping;
        renderer.toneMappingExposure = 1.2; // Slightly brighter
        
        // MAXIMUM QUALITY SETTINGS
        renderer.physicallyCorrectLights = true;
        renderer.useLegacyLights = false;
        renderer.localClippingEnabled = true;
        renderer.sortObjects = true;
        
        // SMART VISUAL ENHANCEMENTS
        renderer.capabilities.logarithmicDepthBuffer = true;
        renderer.info.autoReset = false;
        
        // Enable GPU-based optimizations
        renderer.compile(scene, camera);
        
        rendererRef.current = renderer;
        
        // 🎯 FIX: Canvas BEHIND UI elements!
        renderer.domElement.style.position = 'absolute';
        renderer.domElement.style.top = '0';
        renderer.domElement.style.left = '0';
        renderer.domElement.style.zIndex = '1'; // BEHIND UI (UI has higher z-index)
        renderer.domElement.style.pointerEvents = 'auto'; // Canvas can receive mouse events
        
        container.appendChild(renderer.domElement);
        
        // 🚀 NEXT-LEVEL POST-PROCESSING - Game Engine Quality!
        let composer, bloomPass, ssaoPass;
        
        const setupAdvancedPostProcessing = async () => {
          try {
            // Import QUALITY post-processing modules
            const { EffectComposer } = await import('https://unpkg.com/three@0.180.0/examples/jsm/postprocessing/EffectComposer.js');
            const { RenderPass } = await import('https://unpkg.com/three@0.180.0/examples/jsm/postprocessing/RenderPass.js');
            const { SSAOPass } = await import('https://unpkg.com/three@0.180.0/examples/jsm/postprocessing/SSAOPass.js');
            
            // Create composer
            composer = new EffectComposer(renderer);
            
            // Basic render pass
            const renderPass = new RenderPass(scene, camera);
            composer.addPass(renderPass);
            
            // SSAO for realistic depth and ambient occlusion
            ssaoPass = new SSAOPass(scene, camera, width, height);
            ssaoPass.kernelRadius = 12; // Stronger effect
            ssaoPass.minDistance = 0.002; // More detailed
            ssaoPass.maxDistance = 0.08; // Better range
            ssaoPass.output = SSAOPass.OUTPUT.Default;
            composer.addPass(ssaoPass);
            
            console.log("🎯 SSAO Quality Enhancement Active!");
            
          } catch (error) {
            console.log("📱 Fallback to standard rendering");
            // Fallback to enhanced CSS
            renderer.domElement.style.filter = 'contrast(1.1) saturate(1.2) brightness(1.05)';
          }
        };
        
        setupAdvancedPostProcessing();

        // Camera controls
        const controls = new OrbitControls(camera, renderer.domElement);
        controls.enableDamping = true;
        controls.dampingFactor = 0.08;
        controls.target.set(6, 1, 4);
        controls.maxPolarAngle = Math.PI * 0.45;
        controls.minDistance = 8;
        controls.maxDistance = 30;
        controls.enablePan = true;
        controls.panSpeed = 0.5;
        controlsRef.current = controls;

        setDebugStatus(prev => ({ ...prev, threeJsRunning: true }));

        // Create lighting system
        const lightingSystem = await setupFunctionalLighting(scene);
        lightingSystemRef.current = lightingSystem;
        setDebugStatus(prev => ({ ...prev, lightingActive: true }));

        // Create materials
        const materials = await createFunctionalMaterials();
        materialsRef.current = materials;
        setDebugStatus(prev => ({ ...prev, materialsLoaded: true }));

        // Build apartment with entrance
        await buildApartmentWithEntrance(scene, materials);
        setDebugStatus(prev => ({ ...prev, sceneBuilt: true, controlsConnected: true }));

        // Animation loop with functional controls
        const animate = () => {
          if (!rendererRef.current || !sceneRef.current || !cameraRef.current) return;
          
          if (controlsRef.current) {
            controlsRef.current.update();
          }

          // Apply functional lighting updates
          if (lightingSystemRef.current) {
            updateFunctionalLighting(lightingSystemRef.current);
          }
          
          // 🚀 NEXT-LEVEL RENDERING with Post-Processing
          if (composer) {
            composer.render();
          } else {
            rendererRef.current.render(sceneRef.current, cameraRef.current);
          }
          animationRef.current = requestAnimationFrame(animate);
        };

        // Resize handler
        const handleResize = () => {
          if (!container || !cameraRef.current || !rendererRef.current) return;
          const newWidth = window.innerWidth;
          const newHeight = window.innerHeight;
          cameraRef.current.aspect = newWidth / newHeight;
          cameraRef.current.updateProjectionMatrix();
          rendererRef.current.setSize(newWidth, newHeight);
        };

        window.addEventListener('resize', handleResize);
        animate();
        
        console.log('✅ 3D Viewer mit funktionalen Kontrollen und Eingangstür bereit!');

        return () => {
          window.removeEventListener('resize', handleResize);
          if (animationRef.current) {
            cancelAnimationFrame(animationRef.current);
          }
          if (renderer && container && renderer.domElement && container.contains(renderer.domElement)) {
            container.removeChild(renderer.domElement);
          }
          if (renderer) {
            renderer.dispose();
          }
        };

      } catch (error) {
        console.error('💥 Viewer Error:', error);
        setDebugStatus(prev => ({ ...prev, error: error.message }));
      }
    };

    const cleanup = initViewerWithEntrance();
    return () => {
      if (cleanup && typeof cleanup.then === 'function') {
        cleanup.then(cleanupFn => cleanupFn && cleanupFn());
      } else if (typeof cleanup === 'function') {
        cleanup();
      }
    };
  }, [isActive]);

  // Update lighting when controls change - NOW FUNCTIONAL!
  useEffect(() => {
    if (lightingSystemRef.current && materialsRef.current) {
      updateControlledLighting();
      updateControlledMaterials();
    }
  }, [controlPanel]);

  const updateControlledLighting = () => {
    const lighting = lightingSystemRef.current;
    if (!lighting) return;

    // Apply lighting intensity control
    lighting.sunLight.intensity = 2.5 * controlPanel.lightingIntensity;
    lighting.skyLight.intensity = 0.4 * controlPanel.ambientIntensity;
    
    // Apply time of day
    const timeColor = new THREE.Color().lerpColors(
      new THREE.Color(0xfff4e6), // Day
      new THREE.Color(0xff8c42), // Evening
      1 - controlPanel.timeOfDay
    );
    lighting.sunLight.color = timeColor;
    
    // Update interior lights
    lighting.interiorLights.forEach((light, index) => {
      light.intensity = (0.5 + index * 0.1) * controlPanel.timeOfDay;
    });

    // Update renderer exposure
    if (rendererRef.current) {
      rendererRef.current.toneMappingExposure = 0.8 + (controlPanel.timeOfDay * 0.4);
    }
  };

  const updateControlledMaterials = () => {
    // Update BRIGHT window transparency in real-time
    windowGlassesRef.current.forEach(glass => {
      if (glass.material) {
        // Keep windows bright and visible
        glass.material.opacity = Math.max(0.2, controlPanel.windowTransparency); // Never fully transparent
        glass.material.transmission = 0.85 - (controlPanel.windowTransparency * 0.2); // Less transmission variation
        glass.material.emissiveIntensity = 0.1 + (controlPanel.windowTransparency * 0.05); // Brighter with more transparency
        glass.material.needsUpdate = true;
      }
    });
  };

  const setupFunctionalLighting = async (scene) => {
    console.log("💡 Setup Functional Lighting System...");
    
    const lightingSystem = {
      sunLight: null,
      skyLight: null,
      fillLight: null,
      interiorLights: [],
      animationTime: 0
    };

    // BRIGHT sun light - NO SHADOWS
    lightingSystem.sunLight = new THREE.DirectionalLight(0xffffff, 3.0); // Much brighter
    lightingSystem.sunLight.position.set(20, 25, 15);
    lightingSystem.sunLight.castShadow = false; // NO SHADOWS AT ALL
    scene.add(lightingSystem.sunLight);

    // SUPER BRIGHT ambient light - flood everything with light
    lightingSystem.skyLight = new THREE.AmbientLight(0xffffff, 2.5); // MAXIMUM ambient
    scene.add(lightingSystem.skyLight);

    // Multiple BRIGHT fill lights for even illumination
    lightingSystem.fillLight = new THREE.DirectionalLight(0xffffff, 1.5); // Much stronger fill
    lightingSystem.fillLight.position.set(-10, 10, -10);
    lightingSystem.fillLight.castShadow = false; // No shadows from fill light
    scene.add(lightingSystem.fillLight);
    
    // Additional fill lights from all directions
    const fillLight2 = new THREE.DirectionalLight(0xffffff, 1.2);
    fillLight2.position.set(10, 8, 10);
    fillLight2.castShadow = false; // No shadows
    scene.add(fillLight2);
    
    const fillLight3 = new THREE.DirectionalLight(0xffffff, 1.0);
    fillLight3.position.set(0, 15, 0); // Top-down light
    fillLight3.castShadow = false; // No shadows
    scene.add(fillLight3);
    
    const fillLight4 = new THREE.DirectionalLight(0xffffff, 0.8);
    fillLight4.position.set(0, -5, 0); // Bottom-up light
    fillLight4.castShadow = false; // No shadows
    scene.add(fillLight4);

    // BRIGHT Interior lights - NO SHADOWS
    const interiorConfigs = [
      { pos: [4, 2.4, 3], color: 0xffffff, intensity: 2.0, distance: 12 }, // Living room - much brighter
      { pos: [8, 2.2, 2], color: 0xffffff, intensity: 1.8, distance: 10 }, // Kitchen - brighter
      { pos: [3, 2.1, 7], color: 0xffffff, intensity: 1.5, distance: 10 }, // Bedroom - brighter
      { pos: [1, 2.0, 9], color: 0xffffff, intensity: 1.2, distance: 8 }, // Entrance - brighter
      { pos: [5, 2.2, 5.5], color: 0xffffff, intensity: 1.0, distance: 6 } // Bathroom - new light
    ];

    interiorConfigs.forEach(config => {
      const light = new THREE.PointLight(config.color, config.intensity, config.distance);
      light.position.set(...config.pos);
      light.castShadow = false; // NO SHADOWS from interior lights
      scene.add(light);
      lightingSystem.interiorLights.push(light);
    });

    return lightingSystem;
  };

  const createFunctionalMaterials = async () => {
    console.log("🎨 Create Functional Materials...");
    
    // MEGA REALISTIC WOOD TEXTURE with visible planks!
    const createWoodTexture = (baseColor, grainIntensity = 0.1) => {
      const canvas = document.createElement('canvas');
      canvas.width = 1024; // ULTRA high resolution
      canvas.height = 1024;
      const ctx = canvas.getContext('2d');
      
      // Base wood color
      ctx.fillStyle = `rgb(${baseColor.r * 255}, ${baseColor.g * 255}, ${baseColor.b * 255})`;
      ctx.fillRect(0, 0, 1024, 1024);
      
      // DRAW REALISTIC WOOD PLANKS
      const plankWidth = 128; // Width of each plank
      const plankHeight = 1024; // Full height
      
      for (let plankX = 0; plankX < 1024; plankX += plankWidth) {
        // Plank variation
        const plankVariation = (Math.random() - 0.5) * 0.1;
        const plankColor = {
          r: Math.max(0, Math.min(1, baseColor.r + plankVariation)),
          g: Math.max(0, Math.min(1, baseColor.g + plankVariation)),
          b: Math.max(0, Math.min(1, baseColor.b + plankVariation))
        };
        
        ctx.fillStyle = `rgb(${plankColor.r * 255}, ${plankColor.g * 255}, ${plankColor.b * 255})`;
        ctx.fillRect(plankX, 0, plankWidth - 2, plankHeight); // -2 for joint gap
        
        // WOOD GRAIN within each plank
        for (let y = 0; y < plankHeight; y += 4) {
          const grainIntensity = Math.sin(y * 0.02) * 0.1;
          const grainColor = Math.floor((plankColor.r + grainIntensity) * 255);
          ctx.strokeStyle = `rgb(${grainColor}, ${grainColor}, ${grainColor})`;
          ctx.lineWidth = 1;
          ctx.beginPath();
          ctx.moveTo(plankX + 2, y);
          ctx.lineTo(plankX + plankWidth - 4, y);
          ctx.stroke();
        }
        
        // PLANK JOINTS (darker lines)
        ctx.strokeStyle = `rgb(${baseColor.r * 200}, ${baseColor.g * 200}, ${baseColor.b * 200})`;
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.moveTo(plankX + plankWidth - 1, 0);
        ctx.lineTo(plankX + plankWidth - 1, plankHeight);
        ctx.stroke();
      }
      
      const texture = new THREE.CanvasTexture(canvas);
      texture.wrapS = THREE.RepeatWrapping;
      texture.wrapT = THREE.RepeatWrapping;
      texture.repeat.set(2, 2); // Larger planks
      texture.generateMipmaps = true;
      return texture;
    };
    
    // MEGA REALISTIC MARBLE TEXTURE with veins!
    const createMarbleTexture = () => {
      const canvas = document.createElement('canvas');
      canvas.width = 1024;
      canvas.height = 1024;
      const ctx = canvas.getContext('2d');
      
      // Base marble color (warm white)
      ctx.fillStyle = '#f8f6f0';
      ctx.fillRect(0, 0, 1024, 1024);
      
      // MARBLE VEINS
      const drawVein = (startX, startY, endX, endY, thickness, opacity) => {
        ctx.strokeStyle = `rgba(180, 170, 160, ${opacity})`;
        ctx.lineWidth = thickness;
        ctx.lineCap = 'round';
        ctx.beginPath();
        
        // Curved vein path
        const controlX = (startX + endX) / 2 + (Math.random() - 0.5) * 200;
        const controlY = (startY + endY) / 2 + (Math.random() - 0.5) * 200;
        
        ctx.moveTo(startX, startY);
        ctx.quadraticCurveTo(controlX, controlY, endX, endY);
        ctx.stroke();
      };
      
      // Draw multiple marble veins
      for (let i = 0; i < 8; i++) {
        drawVein(
          Math.random() * 1024, Math.random() * 1024,
          Math.random() * 1024, Math.random() * 1024,
          Math.random() * 3 + 1, Math.random() * 0.3 + 0.1
        );
      }
      
      const texture = new THREE.CanvasTexture(canvas);
      texture.wrapS = THREE.RepeatWrapping;
      texture.wrapT = THREE.RepeatWrapping;
      texture.repeat.set(1, 1);
      texture.generateMipmaps = true;
      return texture;
    };

    const materials = {
      // MEGA REALISTIC FLOOR MATERIALS with visible details!
      oakFloor: new THREE.MeshStandardMaterial({
        name: 'Ultra Realistic Oak Parquet with Planks',
        color: 0xc8a882, // Warmer, more natural oak
        map: createWoodTexture({r: 0.78, g: 0.66, b: 0.51}, 0.2), // MEGA realistic planks
        roughness: 0.25, // Polished but with texture
        metalness: 0.0,
        normalScale: new THREE.Vector2(1.2, 1.2), // Strong texture depth
        envMapIntensity: 0.4, // Subtle wood reflections
        clearcoat: 0.2, // Light protective coating
        clearcoatRoughness: 0.3
      }),
      
      marbleFloor: new THREE.MeshStandardMaterial({
        name: 'Carrara Marble with Natural Veins',
        color: 0xf8f6f0, // Warmer white marble
        map: createMarbleTexture(), // MEGA realistic marble with veins
        roughness: 0.02, // Ultra smooth and polished
        metalness: 0.0,
        transparent: false, // Solid marble
        envMapIntensity: 1.0, // Strong reflections like real marble
        clearcoat: 1.0, // Maximum gloss finish
        clearcoatRoughness: 0.05 // Mirror-like finish
      }),

      // Wall material
      wall: new THREE.MeshStandardMaterial({ 
        name: 'Wall',
        color: 0xfafafa,
        roughness: 0.9,
        metalness: 0.0,
        transparent: true,
        opacity: 0.98
      }),

      // Ceiling
      ceiling: new THREE.MeshStandardMaterial({ 
        name: 'Ceiling',
        color: 0xffffff,
        roughness: 0.95,
        metalness: 0.0
      }),

      // NATURAL SKY WINDOW GLASS - Like real windows!
      windowGlass: new THREE.MeshStandardMaterial({
        name: 'Natural Sky Window Glass',
        color: 0x87CEEB, // Sky blue like real windows
        metalness: 0.0,
        roughness: 0.02, // Very smooth glass
        transparent: true,
        opacity: 0.25, // Much more transparent
        side: THREE.DoubleSide,
        emissive: 0x4682B4, // Soft steel blue glow
        emissiveIntensity: 0.08, // Subtle natural glow
        envMapIntensity: 0.8 // Natural reflections
      }),

      // Window frame
      windowFrame: new THREE.MeshStandardMaterial({
        name: 'Window Frame',
        color: 0x4a4a4a,
        roughness: 0.3,
        metalness: 0.1
      }),

      // PREMIUM FURNITURE MATERIALS
      furniture: new THREE.MeshStandardMaterial({ 
        name: 'Premium Walnut Furniture',
        color: 0x8b6f47, // Rich walnut brown
        map: createWoodTexture({r: 0.55, g: 0.44, b: 0.28}, 0.25), // Rich wood grain
        roughness: 0.2, // Polished furniture finish
        metalness: 0.0,
        normalScale: new THREE.Vector2(0.6, 0.6),
        envMapIntensity: 0.4, // Subtle reflections on polished wood
        clearcoat: 0.3, // Light protective coating
        clearcoatRoughness: 0.4
      }),

      // Door materials
      entranceDoor: new THREE.MeshStandardMaterial({
        name: 'Entrance Door',
        color: 0x654321,
        map: createWoodTexture({r: 0.4, g: 0.26, b: 0.13}, 0.3),
        roughness: 0.3,
        metalness: 0.0
      }),

      interiorDoor: new THREE.MeshStandardMaterial({
        name: 'Interior Door',
        color: 0x8b6f47,
        map: createWoodTexture({r: 0.55, g: 0.44, b: 0.28}, 0.25),
        roughness: 0.4,
        metalness: 0.0
      }),

      doorHandle: new THREE.MeshStandardMaterial({
        name: 'Door Handle',
        color: 0xc0c0c0,
        roughness: 0.2,
        metalness: 0.8
      }),

      // Door glass for entrance
      doorGlass: new THREE.MeshPhysicalMaterial({
        name: 'Door Glass',
        color: 0xf0f8ff,
        metalness: 0.0,
        roughness: 0.05,
        transmission: 0.85,
        transparent: true,
        opacity: 0.2,
        ior: 1.52,
        side: THREE.DoubleSide
      })
    };

    return materials;
  };

  const buildApartmentWithEntrance = async (scene, materials) => {
    console.log("🏗️ Build Apartment with Proper Entrance...");
    
    // Realistic apartment with entrance hall
    const rooms = [
      { id: 'entrance', x: 0, z: 8, width: 2, depth: 2, height: 2.6, floor: 'marble' }, // Entrance hall
      { id: 'living', x: 2, z: 0, width: 6, depth: 4.5, height: 2.6, floor: 'oak' },
      { id: 'kitchen', x: 8, z: 0, width: 3.5, depth: 3, height: 2.6, floor: 'marble' },
      { id: 'bedroom', x: 0, z: 4.5, width: 4, depth: 3.5, height: 2.6, floor: 'oak' },
      { id: 'bathroom', x: 4, z: 4.5, width: 2.5, depth: 2, height: 2.6, floor: 'marble' },
      { id: 'hallway', x: 4, z: 6.5, width: 2.5, depth: 1.5, height: 2.6, floor: 'oak' }
    ];

    const wallThickness = 0.18; // REALISTIC 18cm wall thickness

    // Create floors and ceilings
    rooms.forEach(room => {
      const floorMaterial = room.floor === 'oak' ? materials.oakFloor : materials.marbleFloor;
      
      // Floor
      const floorGeometry = new THREE.PlaneGeometry(room.width, room.depth);
      const floor = new THREE.Mesh(floorGeometry, floorMaterial);
      floor.rotation.x = -Math.PI / 2;
      floor.position.set(room.x + room.width / 2, 0, room.z + room.depth / 2);
      floor.receiveShadow = true;
      scene.add(floor);

      // Ceiling
      const ceilingGeometry = new THREE.PlaneGeometry(room.width, room.depth);
      const ceiling = new THREE.Mesh(ceilingGeometry, materials.ceiling);
      ceiling.rotation.x = Math.PI / 2;
      ceiling.position.set(room.x + room.width / 2, room.height, room.z + room.depth / 2);
      ceiling.receiveShadow = true;
      scene.add(ceiling);
    });

    // Create walls with proper entrance
    createWallsWithEntrance(scene, materials, rooms, wallThickness);
    
    // 🏗️ Add ARCHITECTURAL DETAILS for realism
    addArchitecturalDetails(scene, materials, rooms, wallThickness);

    // Add furniture
    addRealisticFurniture(scene, materials);
  };

  const createWallsWithEntrance = (scene, materials, rooms, wallThickness) => {
    const wallHeight = 2.8; // REALISTIC 2.8m ceiling height
    
    // Define walls with entrance door and other openings - FIXED CLOSED ROOMS
    const walls = [
      // Entrance area - MAIN ENTRANCE DOOR
      { start: [0, 8], end: [0, 10], hasEntranceDoor: true, doorWidth: 0.9, doorHeight: 2.1 },
      { start: [0, 10], end: [2, 10] }, // Entrance wall
      { start: [2, 10], end: [2, 8] }, // Entrance wall
      
      // Living room walls - PROPERLY CLOSED
      { start: [2, 0], end: [8, 0], hasWindow: true, windowWidth: 3, windowHeight: 1.4, windowStart: 0.8 },
      { start: [8, 0], end: [8, 4.5] }, // Closed wall (no door to kitchen)
      { start: [8, 4.5], end: [2, 4.5], hasDoor: true, doorWidth: 0.8, doorHeight: 2.1 }, // Door to hallway
      { start: [2, 4.5], end: [2, 0] }, // Complete living room
      
      // Kitchen walls - PROPERLY CLOSED
      { start: [8, 0], end: [11.5, 0], hasWindow: true, windowWidth: 2, windowHeight: 1.2, windowStart: 1.0 },
      { start: [11.5, 0], end: [11.5, 3] },
      { start: [11.5, 3], end: [8, 3] },
      { start: [8, 3], end: [8, 0] }, // Close kitchen properly
      
      // Bedroom walls - PROPERLY CLOSED
      { start: [0, 4.5], end: [4, 4.5], hasDoor: true, doorWidth: 0.8, doorHeight: 2.1 }, // Door to hallway
      { start: [4, 4.5], end: [4, 8], hasWindow: true, windowWidth: 1.8, windowHeight: 1.3, windowStart: 0.9 },
      { start: [4, 8], end: [2, 8] }, // Connect to entrance
      { start: [2, 8], end: [0, 8] }, // Close entrance connection
      { start: [0, 8], end: [0, 4.5] }, // Complete bedroom
      
      // Bathroom walls - PROPERLY CLOSED
      { start: [4, 4.5], end: [6.5, 4.5] },
      { start: [6.5, 4.5], end: [6.5, 6.5] },
      { start: [6.5, 6.5], end: [4, 6.5], hasDoor: true, doorWidth: 0.7, doorHeight: 2.1 }, // Bathroom door
      { start: [4, 6.5], end: [4, 4.5] }, // Close bathroom
      
      // Hallway walls - PROPERLY CLOSED
      { start: [4, 6.5], end: [6.5, 6.5] },
      { start: [6.5, 6.5], end: [6.5, 8] },
      { start: [6.5, 8], end: [4, 8] },
      { start: [4, 8], end: [4, 6.5] } // Close hallway
    ];

    walls.forEach(wall => {
      const length = Math.sqrt(
        Math.pow(wall.end[0] - wall.start[0], 2) + 
        Math.pow(wall.end[1] - wall.start[1], 2)
      );

      if (length > 0.1) {
        if (wall.hasEntranceDoor) {
          createEntranceDoor(scene, wall, length, wallHeight, wallThickness, materials);
        } else if (wall.hasWindow) {
          createControllableWindow(scene, wall, length, wallHeight, wallThickness, materials);
        } else if (wall.hasDoor) {
          createInteriorDoor(scene, wall, length, wallHeight, wallThickness, materials);
        } else {
          createSolidWall(scene, wall, length, wallHeight, wallThickness, materials);
        }
      }
    });
  };

  const createEntranceDoor = (scene, wall, wallLength, wallHeight, wallThickness, materials) => {
    console.log("🚪 Creating Entrance Door...");
    
    const centerX = (wall.start[0] + wall.end[0]) / 2;
    const centerZ = (wall.start[1] + wall.end[1]) / 2;
    const angle = Math.atan2(wall.end[1] - wall.start[1], wall.end[0] - wall.start[0]);
    
    const doorWidth = wall.doorWidth;
    const doorHeight = wall.doorHeight;
    
    // Create wall segments around entrance door
    const leftWallWidth = (wallLength - doorWidth) / 2;
    
    // Left wall segment
    if (leftWallWidth > 0.1) {
      const leftWallGeometry = new THREE.BoxGeometry(leftWallWidth, wallHeight, wallThickness);
      leftWallGeometry.translate(-doorWidth/2 - leftWallWidth/2, wallHeight/2, 0);
      const leftWall = new THREE.Mesh(leftWallGeometry, materials.wall);
      leftWall.position.set(centerX, 0, centerZ);
      leftWall.rotation.y = angle;
      leftWall.castShadow = true;
      leftWall.receiveShadow = true;
      scene.add(leftWall);
    }
    
    // Right wall segment
    if (leftWallWidth > 0.1) {
      const rightWallGeometry = new THREE.BoxGeometry(leftWallWidth, wallHeight, wallThickness);
      rightWallGeometry.translate(doorWidth/2 + leftWallWidth/2, wallHeight/2, 0);
      const rightWall = new THREE.Mesh(rightWallGeometry, materials.wall);
      rightWall.position.set(centerX, 0, centerZ);
      rightWall.rotation.y = angle;
      rightWall.castShadow = true;
      rightWall.receiveShadow = true;
      scene.add(rightWall);
    }
    
    // Top wall segment
    const topWallHeight = wallHeight - doorHeight;
    if (topWallHeight > 0.1) {
      const topWallGeometry = new THREE.BoxGeometry(doorWidth, topWallHeight, wallThickness);
      topWallGeometry.translate(0, doorHeight + topWallHeight/2, 0);
      const topWall = new THREE.Mesh(topWallGeometry, materials.wall);
      topWall.position.set(centerX, 0, centerZ);
      topWall.rotation.y = angle;
      topWall.castShadow = true;
      topWall.receiveShadow = true;
      scene.add(topWall);
    }
    
    // Entrance door frame - more robust
    const frameThickness = 0.05;
    const frameDepth = wallThickness + 0.02;
    
    const doorFrameGeometry = new THREE.BoxGeometry(doorWidth + frameThickness*2, doorHeight + frameThickness, frameDepth);
    doorFrameGeometry.translate(0, doorHeight/2 + frameThickness/2, 0);
    const doorFrame = new THREE.Mesh(doorFrameGeometry, materials.windowFrame);
    doorFrame.position.set(centerX, 0, centerZ);
    doorFrame.rotation.y = angle;
    doorFrame.castShadow = true;
    doorFrame.receiveShadow = true;
    scene.add(doorFrame);
    
    // Entrance door panel - solid and secure looking
    const doorPanelGeometry = new THREE.BoxGeometry(doorWidth - 0.01, doorHeight - 0.01, 0.05);
    doorPanelGeometry.translate(0, doorHeight/2, wallThickness/2 + 0.025);
    const doorPanel = new THREE.Mesh(doorPanelGeometry, materials.entranceDoor);
    doorPanel.position.set(centerX, 0, centerZ);
    doorPanel.rotation.y = angle;
    doorPanel.castShadow = true;
    doorPanel.receiveShadow = true;
    scene.add(doorPanel);
    
    // Door handle - entrance style
    const handleGeometry = new THREE.BoxGeometry(0.12, 0.03, 0.03);
    handleGeometry.translate(doorWidth/2 - 0.1, doorHeight/2, wallThickness/2 + 0.06);
    const doorHandle = new THREE.Mesh(handleGeometry, materials.doorHandle);
    doorHandle.position.set(centerX, 0, centerZ);
    doorHandle.rotation.y = angle;
    doorHandle.castShadow = true;
    scene.add(doorHandle);
    
    // Door lock
    const lockGeometry = new THREE.CylinderGeometry(0.02, 0.02, 0.04, 8);
    lockGeometry.rotateZ(Math.PI / 2);
    lockGeometry.translate(doorWidth/2 - 0.1, doorHeight/2 - 0.1, wallThickness/2 + 0.06);
    const doorLock = new THREE.Mesh(lockGeometry, materials.doorHandle);
    doorLock.position.set(centerX, 0, centerZ);
    doorLock.rotation.y = angle;
    doorLock.castShadow = true;
    scene.add(doorLock);
    
    // Small glass window in entrance door
    const glassGeometry = new THREE.PlaneGeometry(doorWidth * 0.3, doorHeight * 0.2);
    glassGeometry.translate(0, doorHeight * 0.75, wallThickness/2 + 0.03);
    const doorGlass = new THREE.Mesh(glassGeometry, materials.doorGlass);
    doorGlass.position.set(centerX, 0, centerZ);
    doorGlass.rotation.y = angle;
    scene.add(doorGlass);
  };

  const createControllableWindow = (scene, wall, wallLength, wallHeight, wallThickness, materials) => {
    const centerX = (wall.start[0] + wall.end[0]) / 2;
    const centerZ = (wall.start[1] + wall.end[1]) / 2;
    const angle = Math.atan2(wall.end[1] - wall.start[1], wall.end[0] - wall.start[0]);
    
    const windowWidth = Math.min(wall.windowWidth, wallLength - 0.4);
    const windowHeight = wall.windowHeight;
    const windowStart = wall.windowStart;
    
    // Create wall segments around window
    const leftWallWidth = (wallLength - windowWidth) / 2;
    
    // Left wall segment
    if (leftWallWidth > 0.1) {
      const leftWallGeometry = new THREE.BoxGeometry(leftWallWidth, wallHeight, wallThickness);
      leftWallGeometry.translate(-windowWidth/2 - leftWallWidth/2, wallHeight/2, 0);
      const leftWall = new THREE.Mesh(leftWallGeometry, materials.wall);
      leftWall.position.set(centerX, 0, centerZ);
      leftWall.rotation.y = angle;
      leftWall.castShadow = true;
      leftWall.receiveShadow = true;
      scene.add(leftWall);
    }
    
    // Right wall segment
    if (leftWallWidth > 0.1) {
      const rightWallGeometry = new THREE.BoxGeometry(leftWallWidth, wallHeight, wallThickness);
      rightWallGeometry.translate(windowWidth/2 + leftWallWidth/2, wallHeight/2, 0);
      const rightWall = new THREE.Mesh(rightWallGeometry, materials.wall);
      rightWall.position.set(centerX, 0, centerZ);
      rightWall.rotation.y = angle;
      rightWall.castShadow = true;
      rightWall.receiveShadow = true;
      scene.add(rightWall);
    }
    
    // Bottom wall segment
    if (windowStart > 0.1) {
      const bottomWallGeometry = new THREE.BoxGeometry(windowWidth, windowStart, wallThickness);
      bottomWallGeometry.translate(0, windowStart/2, 0);
      const bottomWall = new THREE.Mesh(bottomWallGeometry, materials.wall);
      bottomWall.position.set(centerX, 0, centerZ);
      bottomWall.rotation.y = angle;
      bottomWall.castShadow = true;
      bottomWall.receiveShadow = true;
      scene.add(bottomWall);
    }
    
    // Top wall segment
    const topWallHeight = wallHeight - windowStart - windowHeight;
    if (topWallHeight > 0.1) {
      const topWallGeometry = new THREE.BoxGeometry(windowWidth, topWallHeight, wallThickness);
      topWallGeometry.translate(0, windowStart + windowHeight + topWallHeight/2, 0);
      const topWall = new THREE.Mesh(topWallGeometry, materials.wall);
      topWall.position.set(centerX, 0, centerZ);
      topWall.rotation.y = angle;
      topWall.castShadow = true;
      topWall.receiveShadow = true;
      scene.add(topWall);
    }
    
    // Window frame
    const frameThickness = 0.05;
    const frameDepth = wallThickness + 0.02;
    
    const frameGeometry = new THREE.BoxGeometry(windowWidth + frameThickness*2, windowHeight + frameThickness*2, frameDepth);
    frameGeometry.translate(0, windowStart + windowHeight/2, 0);
    const frame = new THREE.Mesh(frameGeometry, materials.windowFrame);
    frame.position.set(centerX, 0, centerZ);
    frame.rotation.y = angle;
    frame.castShadow = true;
    frame.receiveShadow = true;
    scene.add(frame);
    
    // Controllable glass - store reference for real-time updates
    const glassGeometry = new THREE.PlaneGeometry(windowWidth - 0.02, windowHeight - 0.02);
    const windowGlass = new THREE.Mesh(glassGeometry, materials.windowGlass.clone());
    windowGlass.position.set(centerX, windowStart + windowHeight/2, centerZ);
    windowGlass.rotation.y = angle;
    
    // Slight offset to prevent z-fighting
    const normal = new THREE.Vector3(0, 0, 1);
    normal.applyAxisAngle(new THREE.Vector3(0, 1, 0), angle);
    windowGlass.position.add(normal.multiplyScalar(0.001));
    
    scene.add(windowGlass);
    
    // Store reference for control updates
    windowGlassesRef.current.push(windowGlass);
  };

  const createInteriorDoor = (scene, wall, wallLength, wallHeight, wallThickness, materials) => {
    const centerX = (wall.start[0] + wall.end[0]) / 2;
    const centerZ = (wall.start[1] + wall.end[1]) / 2;
    const angle = Math.atan2(wall.end[1] - wall.start[1], wall.end[0] - wall.start[0]);
    
    const doorWidth = wall.doorWidth;
    const doorHeight = wall.doorHeight;
    
    // Create wall segments around door
    const leftWallWidth = (wallLength - doorWidth) / 2;
    
    // Left wall segment
    if (leftWallWidth > 0.1) {
      const leftWallGeometry = new THREE.BoxGeometry(leftWallWidth, wallHeight, wallThickness);
      leftWallGeometry.translate(-doorWidth/2 - leftWallWidth/2, wallHeight/2, 0);
      const leftWall = new THREE.Mesh(leftWallGeometry, materials.wall);
      leftWall.position.set(centerX, 0, centerZ);
      leftWall.rotation.y = angle;
      leftWall.castShadow = true;
      leftWall.receiveShadow = true;
      scene.add(leftWall);
    }
    
    // Right wall segment
    if (leftWallWidth > 0.1) {
      const rightWallGeometry = new THREE.BoxGeometry(leftWallWidth, wallHeight, wallThickness);
      rightWallGeometry.translate(doorWidth/2 + leftWallWidth/2, wallHeight/2, 0);
      const rightWall = new THREE.Mesh(rightWallGeometry, materials.wall);
      rightWall.position.set(centerX, 0, centerZ);
      rightWall.rotation.y = angle;
      rightWall.castShadow = true;
      rightWall.receiveShadow = true;
      scene.add(rightWall);
    }
    
    // Top wall segment
    const topWallHeight = wallHeight - doorHeight;
    if (topWallHeight > 0.1) {
      const topWallGeometry = new THREE.BoxGeometry(doorWidth, topWallHeight, wallThickness);
      topWallGeometry.translate(0, doorHeight + topWallHeight/2, 0);
      const topWall = new THREE.Mesh(topWallGeometry, materials.wall);
      topWall.position.set(centerX, 0, centerZ);
      topWall.rotation.y = angle;
      topWall.castShadow = true;
      topWall.receiveShadow = true;
      scene.add(topWall);
    }
    
    // Interior door frame
    const frameThickness = 0.03;
    const frameDepth = wallThickness + 0.01;
    
    const doorFrameGeometry = new THREE.BoxGeometry(doorWidth + frameThickness*2, doorHeight + frameThickness, frameDepth);
    doorFrameGeometry.translate(0, doorHeight/2 + frameThickness/2, 0);
    const doorFrame = new THREE.Mesh(doorFrameGeometry, materials.windowFrame);
    doorFrame.position.set(centerX, 0, centerZ);
    doorFrame.rotation.y = angle;
    doorFrame.castShadow = true;
    doorFrame.receiveShadow = true;
    scene.add(doorFrame);
    
    // Interior door panel
    const doorPanelGeometry = new THREE.BoxGeometry(doorWidth - 0.01, doorHeight - 0.01, 0.03);
    doorPanelGeometry.translate(0, doorHeight/2, wallThickness/2 + 0.015);
    const doorPanel = new THREE.Mesh(doorPanelGeometry, materials.interiorDoor);
    doorPanel.position.set(centerX, 0, centerZ);
    doorPanel.rotation.y = angle;
    doorPanel.castShadow = true;
    doorPanel.receiveShadow = true;
    scene.add(doorPanel);
    
    // Door handle
    const handleGeometry = new THREE.CylinderGeometry(0.015, 0.015, 0.08, 8);
    handleGeometry.rotateZ(Math.PI / 2);
    handleGeometry.translate(doorWidth/2 - 0.08, doorHeight/2, wallThickness/2 + 0.04);
    const doorHandle = new THREE.Mesh(handleGeometry, materials.doorHandle);
    doorHandle.position.set(centerX, 0, centerZ);
    doorHandle.rotation.y = angle;
    doorHandle.castShadow = true;
    scene.add(doorHandle);
  };

  const createSolidWall = (scene, wall, wallLength, wallHeight, wallThickness, materials) => {
    const centerX = (wall.start[0] + wall.end[0]) / 2;
    const centerZ = (wall.start[1] + wall.end[1]) / 2;
    const angle = Math.atan2(wall.end[1] - wall.start[1], wall.end[0] - wall.start[0]);
    
    const wallGeometry = new THREE.BoxGeometry(wallLength, wallHeight, wallThickness);
    wallGeometry.translate(0, wallHeight / 2, 0);
    
    const wallMesh = new THREE.Mesh(wallGeometry, materials.wall);
    wallMesh.position.set(centerX, 0, centerZ);
    wallMesh.rotation.y = angle;
    wallMesh.castShadow = true;
    wallMesh.receiveShadow = true;
    
    scene.add(wallMesh);
  };
  
  // 🏗️ ARCHITECTURAL DETAILS - Professional finishing touches
  const addArchitecturalDetails = (scene, materials, rooms, wallThickness) => {
    console.log("🏗️ Adding Architectural Details - FIXED ALIGNMENT...");
    
    // Create BASEBOARD material
    const baseboardMaterial = new THREE.MeshStandardMaterial({
      name: 'White Baseboard',
      color: 0xf8f8f8,
      roughness: 0.2,
      metalness: 0.0
    });
    
    // 🎯 BASEBOARDS ALIGNED TO ACTUAL WALLS (not room boxes!)
    const baseboardHeight = 0.12; // 12cm baseboard
    const baseboardDepth = 0.02; // 2cm depth
    
    // Define EXACT wall positions for baseboards (matching wall definitions)
    const baseboardWalls = [
      // Living room walls
      { start: [2, 0], end: [8, 0] }, // Front wall
      { start: [8, 0], end: [8, 4.5] }, // Right wall
      { start: [8, 4.5], end: [2, 4.5] }, // Back wall (with door - skip door area)
      { start: [2, 4.5], end: [2, 0] }, // Left wall
      
      // Kitchen walls
      { start: [8, 0], end: [11.5, 0] }, // Front wall
      { start: [11.5, 0], end: [11.5, 3] }, // Right wall
      { start: [11.5, 3], end: [8, 3] }, // Back wall
      { start: [8, 3], end: [8, 0] }, // Left wall
      
      // Bedroom walls
      { start: [0, 4.5], end: [4, 4.5] }, // Front wall (with door - skip door area)
      { start: [4, 4.5], end: [4, 8] }, // Right wall
      { start: [4, 8], end: [2, 8] }, // Back wall
      { start: [2, 8], end: [0, 8] }, // Connection wall
      { start: [0, 8], end: [0, 4.5] }, // Left wall
      
      // Bathroom walls
      { start: [4, 4.5], end: [6.5, 4.5] }, // Front wall
      { start: [6.5, 4.5], end: [6.5, 6.5] }, // Right wall
      { start: [6.5, 6.5], end: [4, 6.5] }, // Back wall (with door - skip door area)
      { start: [4, 6.5], end: [4, 4.5] }, // Left wall
      
      // Entrance area walls
      { start: [0, 8], end: [0, 10] }, // Entrance wall (with door - skip door area)
      { start: [0, 10], end: [2, 10] }, // Top wall
      { start: [2, 10], end: [2, 8] } // Right wall
    ];
    
    baseboardWalls.forEach(wall => {
      const length = Math.sqrt(
        Math.pow(wall.end[0] - wall.start[0], 2) + 
        Math.pow(wall.end[1] - wall.start[1], 2)
      );
      
      if (length > 0.1) {
        const centerX = (wall.start[0] + wall.end[0]) / 2;
        const centerZ = (wall.start[1] + wall.end[1]) / 2;
        const angle = Math.atan2(wall.end[1] - wall.start[1], wall.end[0] - wall.start[0]);
        
        // Position baseboard INSIDE the room, against the wall (FIXED DIRECTION!)
        const offsetX = -Math.sin(angle) * (wallThickness / 2 + baseboardDepth / 2);
        const offsetZ = Math.cos(angle) * (wallThickness / 2 + baseboardDepth / 2);
        
        const baseboardGeometry = new THREE.BoxGeometry(length, baseboardHeight, baseboardDepth);
        baseboardGeometry.translate(0, baseboardHeight / 2, 0);
        
        const baseboard = new THREE.Mesh(baseboardGeometry, baseboardMaterial);
        baseboard.position.set(centerX + offsetX, 0, centerZ + offsetZ);
        baseboard.rotation.y = angle;
        baseboard.castShadow = true;
        baseboard.receiveShadow = true;
        
        scene.add(baseboard);
      }
    });
    
    console.log("✅ Baseboards properly aligned to walls!");
  };

  const addRealisticFurniture = (scene, materials) => {
    // 🔥 ULTRA-PREMIUM LIVING ROOM - Designer Quality Möbel!
    
    // Create PREMIUM FABRIC TEXTURE for sofa
    const createFabricTexture = () => {
      const canvas = document.createElement('canvas');
      canvas.width = 512;
      canvas.height = 512;
      const ctx = canvas.getContext('2d');
      
      // Base grey fabric color
      ctx.fillStyle = '#8a8a8a';
      ctx.fillRect(0, 0, 512, 512);
      
      // Fabric weave pattern
      for (let x = 0; x < 512; x += 4) {
        for (let y = 0; y < 512; y += 4) {
          const brightness = 0.9 + (Math.random() * 0.2);
          ctx.fillStyle = `rgba(138, 138, 138, ${brightness})`;
          ctx.fillRect(x, y, 2, 2);
        }
      }
      
      const texture = new THREE.CanvasTexture(canvas);
      texture.wrapS = THREE.RepeatWrapping;
      texture.wrapT = THREE.RepeatWrapping;
      texture.repeat.set(8, 8);
      return texture;
    };
    
    // PREMIUM SOFA MATERIAL - Ultra realistic grey fabric
    const premiumSofaMaterial = new THREE.MeshStandardMaterial({
      name: 'Premium Grey Fabric',
      color: 0x7a7a7a, // Modern grey
      map: createFabricTexture(),
      roughness: 0.8, // Fabric texture
      metalness: 0.0,
      normalScale: new THREE.Vector2(0.5, 0.5),
      envMapIntensity: 0.1
    });
    
    // ULTRA-PREMIUM L-SHAPED DESIGNER SOFA
    
    // Main sofa base with rounded edges
    const sofaMainGeometry = new THREE.BoxGeometry(2.4, 0.85, 1.0);
    const sofaMain = new THREE.Mesh(sofaMainGeometry, premiumSofaMaterial);
    sofaMain.position.set(3.5, 0.425, 1.5);
    sofaMain.castShadow = true;
    sofaMain.receiveShadow = true;
    scene.add(sofaMain);
    
    // L-extension with perfect connection
    const sofaExtensionGeometry = new THREE.BoxGeometry(1.0, 0.85, 1.4);
    const sofaExtension = new THREE.Mesh(sofaExtensionGeometry, premiumSofaMaterial);
    sofaExtension.position.set(4.7, 0.425, 2.5);
    sofaExtension.castShadow = true;
    sofaExtension.receiveShadow = true;
    scene.add(sofaExtension);
    
    // PREMIUM BACK CUSHIONS with realistic depth
    const backCushion1Geometry = new THREE.BoxGeometry(2.4, 0.8, 0.25);
    const backCushion1 = new THREE.Mesh(backCushion1Geometry, premiumSofaMaterial);
    backCushion1.position.set(3.5, 1.0, 1.125);
    backCushion1.castShadow = true;
    scene.add(backCushion1);
    
    const backCushion2Geometry = new THREE.BoxGeometry(0.25, 0.8, 1.4);
    const backCushion2 = new THREE.Mesh(backCushion2Geometry, premiumSofaMaterial);
    backCushion2.position.set(5.075, 1.0, 2.5);
    backCushion2.castShadow = true;
    scene.add(backCushion2);
    
    // DESIGNER ARMRESTS with curved tops
    const armrest1Geometry = new THREE.BoxGeometry(0.2, 0.7, 1.0);
    const armrest1 = new THREE.Mesh(armrest1Geometry, premiumSofaMaterial);
    armrest1.position.set(2.4, 0.775, 1.5);
    armrest1.castShadow = true;
    scene.add(armrest1);
    
    const armrest2Geometry = new THREE.BoxGeometry(0.2, 0.7, 1.0);
    const armrest2 = new THREE.Mesh(armrest2Geometry, premiumSofaMaterial);
    armrest2.position.set(4.6, 0.775, 1.5);
    armrest2.castShadow = true;
    scene.add(armrest2);
    
    // SEAT CUSHIONS with realistic segments
    const seatCushion1Geometry = new THREE.BoxGeometry(1.1, 0.15, 0.8);
    const seatCushion1 = new THREE.Mesh(seatCushion1Geometry, premiumSofaMaterial);
    seatCushion1.position.set(3.0, 0.925, 1.5);
    seatCushion1.castShadow = true;
    scene.add(seatCushion1);
    
    const seatCushion2Geometry = new THREE.BoxGeometry(1.1, 0.15, 0.8);
    const seatCushion2 = new THREE.Mesh(seatCushion2Geometry, premiumSofaMaterial);
    seatCushion2.position.set(4.0, 0.925, 1.5);
    seatCushion2.castShadow = true;
    scene.add(seatCushion2);
    
    const seatCushion3Geometry = new THREE.BoxGeometry(0.8, 0.15, 1.1);
    const seatCushion3 = new THREE.Mesh(seatCushion3Geometry, premiumSofaMaterial);
    seatCushion3.position.set(4.7, 0.925, 2.1);
    seatCushion3.castShadow = true;
    scene.add(seatCushion3);
    
    // ULTRA-PREMIUM ROUND MARBLE COFFEE TABLE
    
    // Round marble top
    const tableTopGeometry = new THREE.CylinderGeometry(0.6, 0.6, 0.05, 32);
    const roundTableTop = new THREE.Mesh(tableTopGeometry, materials.marbleFloor);
    roundTableTop.position.set(5.8, 0.45, 2.2); // Positioned away from sofa
    roundTableTop.castShadow = true;
    roundTableTop.receiveShadow = true;
    scene.add(roundTableTop);
    
    // DESIGNER METAL BASE - Modern tripod
    const metalMaterial = new THREE.MeshStandardMaterial({
      name: 'Brushed Steel',
      color: 0xc0c0c0,
      metalness: 0.9,
      roughness: 0.1,
      envMapIntensity: 1.0
    });
    
    // Three elegant legs in tripod formation
    for (let i = 0; i < 3; i++) {
      const angle = (i * Math.PI * 2) / 3;
      const legX = Math.cos(angle) * 0.35;
      const legZ = Math.sin(angle) * 0.35;
      
      // Angled leg
      const legGeometry = new THREE.CylinderGeometry(0.015, 0.025, 0.4, 8);
      const leg = new THREE.Mesh(legGeometry, metalMaterial);
      leg.position.set(5.8 + legX, 0.2, 2.2 + legZ);
      leg.rotation.z = Math.atan2(legZ, legX) * 0.1; // Slight angle
      leg.castShadow = true;
      scene.add(leg);
    }
    
    // Central support column
    const centralColumnGeometry = new THREE.CylinderGeometry(0.03, 0.03, 0.35, 8);
    const centralColumn = new THREE.Mesh(centralColumnGeometry, metalMaterial);
    centralColumn.position.set(5.8, 0.175, 2.2);
    centralColumn.castShadow = true;
    scene.add(centralColumn);
    
    // 🔥 PREMIUM FURNITURE COMPLETE - No old furniture needed!

    // KITCHEN - Professional setup
    
    // Kitchen counter with realistic height
    const counterBase = new THREE.BoxGeometry(2.8, 0.6, 0.65);
    counterBase.translate(0, 0.3, 0);
    const counterBaseMesh = new THREE.Mesh(counterBase, materials.furniture);
    counterBaseMesh.position.set(9.5, 0, 0.5);
    counterBaseMesh.castShadow = true;
    counterBaseMesh.receiveShadow = true;
    scene.add(counterBaseMesh);
    
    // Counter top (marble)
    const counterTop = new THREE.BoxGeometry(2.8, 0.05, 0.65);
    counterTop.translate(0, 0.625, 0);
    const counterTopMesh = new THREE.Mesh(counterTop, materials.marbleFloor);
    counterTopMesh.position.set(9.5, 0, 0.5);
    counterTopMesh.castShadow = true;
    counterTopMesh.receiveShadow = true;
    scene.add(counterTopMesh);
    
    // Kitchen sink (built into counter)
    const kitchenSink = new THREE.CylinderGeometry(0.2, 0.2, 0.08, 16);
    kitchenSink.translate(0, 0.66, 0);
    const kitchenSinkMesh = new THREE.Mesh(kitchenSink, materials.doorHandle);
    kitchenSinkMesh.position.set(10.5, 0, 0.5);
    kitchenSinkMesh.castShadow = true;
    scene.add(kitchenSinkMesh);

    // BEDROOM - Comfortable and realistic
    
    // 🛏️ MODERN DESIGNER BED - Platform style with floating effect
    
    // Modern platform base with legs (not solid block)
    const platformBase = new THREE.BoxGeometry(1.9, 0.08, 2.2);
    platformBase.translate(0, 0.35, 0);
    const platformMesh = new THREE.Mesh(platformBase, materials.furniture);
    platformMesh.position.set(1.2, 0, 6.5);
    platformMesh.castShadow = true;
    platformMesh.receiveShadow = true;
    scene.add(platformMesh);
    
    // Modern metal legs (4 corners)
    const legPositions = [
      [-0.8, 0, -0.9], [0.8, 0, -0.9], [-0.8, 0, 0.9], [0.8, 0, 0.9]
    ];
    
    legPositions.forEach(pos => {
      const legGeometry = new THREE.CylinderGeometry(0.02, 0.02, 0.35, 8);
      legGeometry.translate(0, 0.175, 0);
      const leg = new THREE.Mesh(legGeometry, materials.doorHandle); // Metal material
      leg.position.set(1.2 + pos[0], 0, 6.5 + pos[2]);
      leg.castShadow = true;
      leg.receiveShadow = true;
      scene.add(leg);
    });
    
    // Premium mattress with visible details
    const mattress = new THREE.BoxGeometry(1.7, 0.25, 2.0);
    mattress.translate(0, 0.52, 0);
    const mattressMesh = new THREE.Mesh(mattress, materials.fabric); // Fabric texture
    mattressMesh.position.set(1.2, 0, 6.5);
    mattressMesh.castShadow = true;
    mattressMesh.receiveShadow = true;
    scene.add(mattressMesh);
    
    // Modern upholstered headboard with padding
    const headboardBase = new THREE.BoxGeometry(1.9, 1.2, 0.12);
    headboardBase.translate(0, 0.9, -1.06);
    const headboardMesh = new THREE.Mesh(headboardBase, materials.fabric);
    headboardMesh.position.set(1.2, 0, 6.5);
    headboardMesh.castShadow = true;
    headboardMesh.receiveShadow = true;
    scene.add(headboardMesh);
    
    // Headboard padding details (tufted effect)
    for (let i = 0; i < 3; i++) {
      for (let j = 0; j < 2; j++) {
        const tufting = new THREE.SphereGeometry(0.03, 8, 6);
        tufting.translate((i - 1) * 0.5, 0.6 + j * 0.4, -0.99);
        const tuft = new THREE.Mesh(tufting, materials.doorHandle);
        tuft.position.set(1.2, 0, 6.5);
        tuft.castShadow = true;
        scene.add(tuft);
      }
    }
    
    // Bedside table
    const nightstand = new THREE.BoxGeometry(0.4, 0.5, 0.3);
    nightstand.translate(0, 0.25, 0);
    const nightstandMesh = new THREE.Mesh(nightstand, materials.furniture);
    nightstandMesh.position.set(2.5, 0, 6.0);
    nightstandMesh.castShadow = true;
    nightstandMesh.receiveShadow = true;
    scene.add(nightstandMesh);

    // PROPER BATHROOM FIXTURES - Every apartment needs these!
    
    // Bathroom sink with cabinet
    const sinkCabinetGeometry = new THREE.BoxGeometry(0.8, 0.6, 0.4);
    sinkCabinetGeometry.translate(0, 0.3, 0);
    const sinkCabinet = new THREE.Mesh(sinkCabinetGeometry, materials.furniture);
    sinkCabinet.position.set(5.5, 0, 5.2);
    sinkCabinet.castShadow = true;
    sinkCabinet.receiveShadow = true;
    scene.add(sinkCabinet);
    
    // Sink basin on top
    const sinkGeometry = new THREE.CylinderGeometry(0.25, 0.25, 0.08, 16);
    sinkGeometry.translate(0, 0.64, 0);
    const sink = new THREE.Mesh(sinkGeometry, materials.marbleFloor);
    sink.position.set(5.5, 0, 5.2);
    sink.castShadow = true;
    sink.receiveShadow = true;
    scene.add(sink);
    
    // Toilet - essential for every bathroom!
    const toiletBaseGeometry = new THREE.BoxGeometry(0.4, 0.4, 0.6);
    toiletBaseGeometry.translate(0, 0.2, 0);
    const toiletBase = new THREE.Mesh(toiletBaseGeometry, materials.marbleFloor);
    toiletBase.position.set(6.0, 0, 6.0);
    toiletBase.castShadow = true;
    toiletBase.receiveShadow = true;
    scene.add(toiletBase);
    
    // Toilet tank
    const toiletTankGeometry = new THREE.BoxGeometry(0.35, 0.3, 0.15);
    toiletTankGeometry.translate(0, 0.55, -0.2);
    const toiletTank = new THREE.Mesh(toiletTankGeometry, materials.marbleFloor);
    toiletTank.position.set(6.0, 0, 6.0);
    toiletTank.castShadow = true;
    toiletTank.receiveShadow = true;
    scene.add(toiletTank);
    
    // Shower/Bathtub area
    const showerBaseGeometry = new THREE.BoxGeometry(0.9, 0.1, 1.4);
    showerBaseGeometry.translate(0, 0.05, 0);
    const showerBase = new THREE.Mesh(showerBaseGeometry, materials.marbleFloor);
    showerBase.position.set(4.5, 0, 5.5);
    showerBase.castShadow = true;
    showerBase.receiveShadow = true;
    scene.add(showerBase);
    
    // Shower walls (glass panels)
    const showerWallGeometry = new THREE.PlaneGeometry(1.4, 2.0);
    const showerWall1 = new THREE.Mesh(showerWallGeometry, materials.windowGlass);
    showerWall1.position.set(4.05, 1.0, 5.5);
    showerWall1.rotation.y = Math.PI / 2;
    scene.add(showerWall1);
    
    const showerWall2 = new THREE.Mesh(showerWallGeometry.clone(), materials.windowGlass);
    showerWall2.position.set(4.5, 1.0, 4.8);
    scene.add(showerWall2);

    // Entrance area - coat rack
    const coatRackGeometry = new THREE.CylinderGeometry(0.02, 0.02, 1.8, 8);
    coatRackGeometry.translate(0, 0.9, 0);
    const coatRack = new THREE.Mesh(coatRackGeometry, materials.doorHandle);
    coatRack.position.set(0.3, 0, 9.5);
    coatRack.castShadow = true;
    coatRack.receiveShadow = true;
    scene.add(coatRack);
  };

  const updateFunctionalLighting = (lightingSystem) => {
    if (!lightingSystem) return;
    
    lightingSystem.animationTime += 0.005;
    
    // Subtle lighting variations
    const timeVariation = Math.sin(lightingSystem.animationTime) * 0.05 + 1;
    
    // Apply control panel values in real-time
    lightingSystem.sunLight.intensity = 2.5 * controlPanel.lightingIntensity * timeVariation;
    lightingSystem.skyLight.intensity = 0.4 * controlPanel.ambientIntensity;
    
    // Update interior lights based on time of day
    lightingSystem.interiorLights.forEach((light, index) => {
      const flicker = Math.sin(lightingSystem.animationTime * 3 + index) * 0.02 + 1;
      light.intensity = (0.5 + index * 0.1) * controlPanel.timeOfDay * flicker;
    });
  };

  // Control handlers
  const handleControlChange = (key, value) => {
    setControlPanel(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const resetControls = () => {
    setControlPanel({
      isExpanded: true,
      lightingIntensity: 1.2,
      ambientIntensity: 0.4,
      windowTransparency: 0.3, // Higher default for brighter windows
      materialQuality: 'premium',
      shadowSoftness: 0.8,
      timeOfDay: 0.7,
      cameraHeight: 1.6
    });
  };

  if (!isActive) return null;

  console.log("🎯 ThreeDViewer rendering with UI components...");
  console.log("🎛️ Control Panel State:", controlPanel);

  return createPortal(
    <div
      ref={mountRef}
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100vw",
        height: "100vh",
        zIndex: 99999, // Higher z-index
        background: "linear-gradient(135deg, #f5f5f0 0%, #e8e8e0 100%)",
      }}
    >
      {/* Status Panel */}
      <div style={{
        position: "absolute",
        top: "15px",
        left: "15px",
        background: "rgba(255,255,255,0.95)",
        color: "#333",
        padding: "20px",
        borderRadius: "15px",
        fontFamily: "'Segoe UI', sans-serif",
        fontSize: "13px",
        minWidth: "280px",
        boxShadow: "0 8px 32px rgba(0,0,0,0.1)",
        backdropFilter: "blur(10px)",
        border: "1px solid rgba(255,255,255,0.2)",
        zIndex: "10", // ABOVE canvas
        pointerEvents: "auto" // Can be clicked
      }}>
        <div style={{ fontWeight: "600", marginBottom: "12px", color: "#2c3e50" }}>
          🚪 Apartment with Entrance Door
        </div>
        <div>Container: {debugStatus.containerReady ? '✅ Ready' : '⏳ Loading...'}</div>
        <div>Engine: {debugStatus.threeJsRunning ? '✅ Running' : '⏳ Starting...'}</div>
        <div>Materials: {debugStatus.materialsLoaded ? '✅ Loaded' : '⏳ Creating...'}</div>
        <div>Lighting: {debugStatus.lightingActive ? '✅ Active' : '⏳ Setting up...'}</div>
        <div>Scene: {debugStatus.sceneBuilt ? '✅ Built' : '⏳ Building...'}</div>
        <div>Controls: {debugStatus.controlsConnected ? '✅ Functional' : '⏳ Connecting...'}</div>
        {debugStatus.error && <div style={{color: '#e74c3c', marginTop: '8px'}}>❌ {debugStatus.error}</div>}
      </div>

      {/* Close Button */}
      <div 
        style={{
          position: "absolute",
          top: "15px",
          right: "15px",
          background: "rgba(231, 76, 60, 0.9)",
          color: "white",
          padding: "12px 16px",
          borderRadius: "10px",
          cursor: "pointer",
          fontSize: "14px",
          fontWeight: "600",
          boxShadow: "0 4px 16px rgba(231, 76, 60, 0.3)",
          zIndex: "10", // ABOVE canvas
          pointerEvents: "auto" // Can be clicked
        }}
        onClick={() => window.location.reload()}
      >
        ✕ Close
      </div>

      {/* Functional Control Panel */}
      <div style={{
        position: "absolute",
        bottom: "20px",
        right: "20px",
        background: "rgba(255,255,255,0.95)",
        color: "#333",
        padding: "24px",
        borderRadius: "20px",
        fontSize: "13px",
        minWidth: "320px",
        maxHeight: "500px",
        boxShadow: "0 12px 40px rgba(0,0,0,0.15)",
        backdropFilter: "blur(15px)",
        border: "1px solid rgba(255,255,255,0.3)",
        overflowY: "auto",
        zIndex: "10", // ABOVE canvas
        pointerEvents: "auto" // Can be clicked and interacted with
      }}>
        <div style={{ fontWeight: "600", marginBottom: "20px", fontSize: "16px", color: "#2c3e50" }}>
          🎛️ Functional Controls
        </div>

        {/* Lighting Controls - NOW FUNCTIONAL */}
        <div style={{ marginBottom: "20px" }}>
          <div style={{ fontWeight: "500", marginBottom: "12px", color: "#34495e" }}>💡 Lighting (Live)</div>
          
          <div style={{ marginBottom: "12px" }}>
            <label style={{ display: "block", marginBottom: "6px", fontSize: "12px", color: "#7f8c8d" }}>
              Light Intensity: {Math.round(controlPanel.lightingIntensity * 100)}%
            </label>
            <input 
              type="range" 
              min="0.3" 
              max="2.0" 
              step="0.1" 
              value={controlPanel.lightingIntensity}
              onChange={(e) => handleControlChange('lightingIntensity', parseFloat(e.target.value))}
              style={{ 
                width: "100%", 
                height: "6px",
                background: "linear-gradient(to right, #f39c12, #e67e22)",
                borderRadius: "3px",
                outline: "none",
                cursor: "pointer"
              }}
            />
          </div>
          
          <div style={{ marginBottom: "12px" }}>
            <label style={{ display: "block", marginBottom: "6px", fontSize: "12px", color: "#7f8c8d" }}>
              Ambient Light: {Math.round(controlPanel.ambientIntensity * 100)}%
            </label>
            <input 
              type="range" 
              min="0.1" 
              max="0.8" 
              step="0.1" 
              value={controlPanel.ambientIntensity}
              onChange={(e) => handleControlChange('ambientIntensity', parseFloat(e.target.value))}
              style={{ 
                width: "100%", 
                height: "6px",
                background: "linear-gradient(to right, #3498db, #2980b9)",
                borderRadius: "3px",
                outline: "none",
                cursor: "pointer"
              }}
            />
          </div>

          <div style={{ marginBottom: "12px" }}>
            <label style={{ display: "block", marginBottom: "6px", fontSize: "12px", color: "#7f8c8d" }}>
              Time of Day: {Math.round(controlPanel.timeOfDay * 100)}%
            </label>
            <input 
              type="range" 
              min="0.1" 
              max="1.0" 
              step="0.1" 
              value={controlPanel.timeOfDay}
              onChange={(e) => handleControlChange('timeOfDay', parseFloat(e.target.value))}
              style={{ 
                width: "100%", 
                height: "6px",
                background: "linear-gradient(to right, #9b59b6, #8e44ad)",
                borderRadius: "3px",
                outline: "none",
                cursor: "pointer"
              }}
            />
          </div>
        </div>

        {/* Material Controls - NOW FUNCTIONAL */}
        <div style={{ marginBottom: "20px" }}>
          <div style={{ fontWeight: "500", marginBottom: "12px", color: "#34495e" }}>🎨 Materials (Live)</div>
          
          <div style={{ marginBottom: "12px" }}>
            <label style={{ display: "block", marginBottom: "6px", fontSize: "12px", color: "#7f8c8d" }}>
              Window Transparency: {Math.round(controlPanel.windowTransparency * 100)}%
            </label>
            <input 
              type="range" 
              min="0.05" 
              max="0.4" 
              step="0.05" 
              value={controlPanel.windowTransparency}
              onChange={(e) => handleControlChange('windowTransparency', parseFloat(e.target.value))}
              style={{ 
                width: "100%", 
                height: "6px",
                background: "linear-gradient(to right, #1abc9c, #16a085)",
                borderRadius: "3px",
                outline: "none",
                cursor: "pointer"
              }}
            />
          </div>
        </div>

        {/* Action Buttons */}
        <div style={{ display: "flex", gap: "12px", marginTop: "20px" }}>
          <button 
            onClick={resetControls}
            style={{
              flex: 1,
              padding: "12px 16px",
              background: "linear-gradient(135deg, #3498db, #2980b9)",
              color: "white",
              border: "none",
              borderRadius: "10px",
              fontSize: "12px",
              cursor: "pointer",
              fontWeight: "500",
              boxShadow: "0 4px 12px rgba(52, 152, 219, 0.3)"
            }}
          >
            🔄 Reset
          </button>
          <button 
            style={{
              flex: 1,
              padding: "12px 16px",
              background: "linear-gradient(135deg, #27ae60, #229954)",
              color: "white",
              border: "none",
              borderRadius: "10px",
              fontSize: "12px",
              cursor: "pointer",
              fontWeight: "500",
              boxShadow: "0 4px 12px rgba(39, 174, 96, 0.3)"
            }}
          >
            📸 Capture
          </button>
        </div>

        <div style={{ 
          marginTop: "16px", 
          fontSize: "11px", 
          color: "#95a5a6", 
          textAlign: "center",
          borderTop: "1px solid #ecf0f1",
          paddingTop: "12px"
        }}>
          🚪 With Entrance Door & Functional Controls
        </div>
      </div>
    </div>,
    document.body
  );
}
